/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import InlineEditable from './inline-editable';
import type { EditableTypes } from './inline-editable';
import Tooltip from './info-elements/tooltip';

type SaveMethodProps = {
  method: any
  params: Record<string, any>
}

interface Props {
  title: string;
  subtitle?: string;
  subtitleEmptyText?: string;
  options?: React.ReactElement[];
  isEditable?: boolean;
  onSave?: SaveMethodProps;
  errors?: string[];
  requiredTitleText?: string;
}

export default function Header({ onSave, requiredTitleText = 'A title is required', ...props }: Props) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false);
  const [errors, setErrors] = useState(props.errors);

  React.useEffect(() => {
    setErrors(props.errors);
  }, [props.errors]);

  const handleSave = async (type: EditableTypes, value?: string) => {
    const isTitleType = type === 'title';
    const property = isTitleType ? 'name' : 'description';

    if (!value?.trim()) {
      setErrors([requiredTitleText]);
      return;
    }

    const res = await onSave?.method({
      ...onSave?.params,
      [property]: value,
    }).unwrap();

    if (!res.data?.errors?.length) {
      if (isTitleType) {
        setIsEditingTitle(false);
      } else {
        setIsEditingSubtitle(false);
      }
    }
  };

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2 }}
        justifyContent="space-between"
        sx={{ width: '100%' }}
      >
        {props.isEditable
          ? (
            <InlineEditable
              value={props.title}
              typographyVariant="h1"
              onSave={handleSave}
              onStartEditing={() => setIsEditingTitle(true)}
              onStopEditing={() => setIsEditingTitle(false)}
              isEditing={isEditingTitle}
              truncate
              errorMessage={errors?.[0]}
              onFocus={() => setErrors(undefined)}
            />
          )
          : (
            <Tooltip>
              <Typography variant="h1">{props.title}</Typography>
            </Tooltip>
          )}
        {!isEditingTitle && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            spacing={{ xs: 1, sm: 2 }}
          >
            {props.options?.map((option) => (
              <Box key={option.key} component="span" sx={{ width: '100%' }}>
                {option}
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
      {(props.subtitle || props.subtitleEmptyText) && (
        <Box sx={{ maxWidth: isEditingSubtitle ? '100%' : 'md', alignItems: 'center' }}>
          {props.isEditable
            ? (
              <InlineEditable
                type="textarea"
                value={props.subtitle}
                emptyText={props.subtitleEmptyText}
                onSave={handleSave}
                isEditing={isEditingSubtitle}
                typographyVariant="body1"
                onStartEditing={() => setIsEditingSubtitle(true)}
                onStopEditing={() => setIsEditingSubtitle(false)}
              />
            )
            : <Typography sx={{ mt: 1 }}>{props.subtitle}</Typography>}
        </Box>
      )}
    </>
  );
}
